import { TextField as MUITextField } from "@material-ui/core";
import InputLabel from "@material-ui/core/InputLabel";
import { Autocomplete } from "@material-ui/lab";
import {
  Bagde,
  useStyles
} from "components/request-document/components/Commons";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAddress, selectAddress } from "redux/modules/addressSlice";
import { canEditForm } from "redux/modules/authSlice";

const TextField = React.memo(MUITextField);

export function useAddress({ formik, ...props }) {
  const ProvinceFieldName = props.ProvinceFieldName || "document.Province";
  const DistrictFieldName = props.DistrictFieldName || "document.District";
  const SubDistrictFieldName =
    props.SubDistrictFieldName || "document.SubDistrict";
  const ZipcodeFieldName = props.ZipcodeFieldName || "document.Zipcode";
  const shouldCheckHasAddress = props.shouldCheckHasAddress ?? true;

  const dispatch = useDispatch();
  const { list: address } = useSelector(selectAddress);
  const [values, setValue] = useState({
    Province: "",
    District: "",
    SubDistrict: "",
    Zipcode: "",
  });
  const [inputValues, setInputValue] = useState({
    Province: "",
    District: "",
    SubDistrict: "",
    Zipcode: "",
  });

  const provinceList = address;
  const districtList =
    provinceList.find((v) => v.provinceNameEng === values.Province)
      ?.listDistrict || [];
  const subDistrictList =
    districtList.find((v) => v.districtNameEng === values.District)
      ?.listSubDistrict || [];

  const zipcodeList =
    subDistrictList.filter(
      (v) => v.subDistrictNameEng === values.SubDistrict
    ) || [];

  useEffect(() => {
    dispatch(getAddress());
  }, []);

  useEffect(() => {
    if (get(formik.values, ZipcodeFieldName) && !values.Zipcode) {
      setValue({
        Province: get(formik.values, ProvinceFieldName) || "",
        District: get(formik.values, DistrictFieldName) || "",
        SubDistrict: get(formik.values, SubDistrictFieldName) || "",
        Zipcode: get(formik.values, ZipcodeFieldName) || "",
      });
      setInputValue({
        Province: get(formik.values, ProvinceFieldName) || "",
        District: get(formik.values, DistrictFieldName) || "",
        SubDistrict: get(formik.values, SubDistrictFieldName) || "",
        Zipcode: get(formik.values, ZipcodeFieldName) || "",
      });
    }
  }, [
    get(formik.values, ProvinceFieldName),
    get(formik.values, DistrictFieldName),
    get(formik.values, SubDistrictFieldName),
    get(formik.values, ZipcodeFieldName),
    values.Zipcode,
  ]);

  const mapOption = (list, fieldName) => list.map((v) => v[fieldName]);
  const handleChange = (fieldName) => (event, newValue) => {
    if (!event) return;
    event.preventDefault();

    if (newValue) {
      let data = {};

      switch (fieldName) {
        case "Province":
          data = {
            Province: newValue,
            District: "",
            SubDistrict: "",
            Zipcode: "",
          };
          break;
        case "District":
          data = {
            ...values,
            District: newValue,
            SubDistrict: "",
            Zipcode: "",
          };
          break;
        case "SubDistrict": {
          const zipcode =
            districtList
              .find((v) => v.districtNameEng === values.District)
              ?.listSubDistrict.find((v) => v.subDistrictNameEng === newValue)
              ?.zipcode || "";

          data = {
            ...values,
            SubDistrict: newValue,
            Zipcode: zipcode,
          };
          break;
        }
        case "Zipcode":
          data = {
            ...values,
            Zipcode: newValue,
          };

          break;
        default:
          break;
      }

      setValue(data);
      setInputValue(data);

      formik.setFieldValue(ProvinceFieldName, data.Province);
      formik.setFieldValue(DistrictFieldName, data.District);
      formik.setFieldValue(SubDistrictFieldName, data.SubDistrict);
      formik.setFieldValue(ZipcodeFieldName, data.Zipcode);
    }
  };

  const handleInputChange = (fieldName) => (event, newValue) => {
    if (!event) return;
    event.preventDefault();

    if (event.type !== "blur") {
      setInputValue((prevValue) => ({ ...prevValue, [fieldName]: newValue }));
    }
  };

  const HasAddressInThai = shouldCheckHasAddress
    ? !formik?.values?.document?.HasAddressInThai
    : false;

  return {
    values,
    inputValues,
    provinceList,
    districtList,
    subDistrictList,
    zipcodeList,
    mapOption,
    handleChange,
    handleInputChange,
    HasAddressInThai,
    formik,
    // HasAddressInThai,
  };
}

export function ProvinceField({
  formik,
  values,
  inputValues,
  mapOption,
  provinceList,
  handleChange,
  handleInputChange,
  HasAddressInThai,
  showBadge,
  disabled,
}) {
  const classes = useStyles();
  const canEdit = useSelector(canEditForm);

  return (
    <div className={classes.cellContent}>
      {showBadge && <Bagde number={0} />}
      <div className={classes.wrapField}>
        <InputLabel>Province</InputLabel>
        <Autocomplete
          id="Province"
          disableClearable
          disabled={disabled || !canEdit}
          value={values.Province}
          inputValue={inputValues.Province}
          options={mapOption(provinceList, "provinceNameEng")}
          onChange={handleChange("Province")}
          onInputChange={handleInputChange("Province")}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textField}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

export function DistrictField({
  formik,
  values,
  inputValues,
  mapOption,
  districtList,
  handleChange,
  handleInputChange,
  HasAddressInThai,
  showBadge,
  disabled,
}) {
  const classes = useStyles();
  const canEdit = useSelector(canEditForm);

  return (
    <div className={classes.cellContent}>
      {showBadge && <Bagde number={0} />}
      <div className={classes.wrapField}>
        <InputLabel>District</InputLabel>
        <Autocomplete
          id="District"
          disableClearable
          disabled={disabled || !canEdit || !districtList.length}
          value={values.District}
          inputValue={inputValues.District}
          options={mapOption(districtList, "districtNameEng")}
          onChange={handleChange("District")}
          onInputChange={handleInputChange("District")}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textField}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

export function SubDistrictField({
  formik,
  values,
  inputValues,
  mapOption,
  subDistrictList,
  handleChange,
  handleInputChange,
  HasAddressInThai,
  showBadge,
  disabled,
}) {
  const classes = useStyles();
  const canEdit = useSelector(canEditForm);

  return (
    <div className={classes.cellContent}>
      {showBadge && <Bagde number={0} />}
      <div className={classes.wrapField}>
        <InputLabel>Sub-district</InputLabel>
        <Autocomplete
          id="Sub-SubDistrict"
          disableClearable
          disabled={disabled || !canEdit || !subDistrictList.length}
          value={values.SubDistrict}
          inputValue={inputValues.SubDistrict}
          options={mapOption(subDistrictList, "subDistrictNameEng")}
          onChange={handleChange("SubDistrict")}
          onInputChange={handleInputChange("SubDistrict")}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textField}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </div>
    </div>
  );
}

export function ZipcodeField({
  formik,
  values,
  inputValues,
  mapOption,
  zipcodeList,
  handleChange,
  handleInputChange,
  HasAddressInThai,
  showBadge,
  disabled,
}) {
  const classes = useStyles();
  const canEdit = useSelector(canEditForm);

  return (
    <div className={classes.cellContent}>
      {showBadge && <Bagde number={0} />}
      <div className={classes.wrapField}>
        <InputLabel>Zip code</InputLabel>
        <Autocomplete
          id="Zipcode"
          disableClearable
          disabled={disabled || !canEdit || !zipcodeList.length}
          value={values.Zipcode}
          inputValue={inputValues.Zipcode}
          options={mapOption(zipcodeList, "zipcode")}
          onChange={handleChange("Zipcode")}
          onInputChange={handleInputChange("Zipcode")}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textField}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </div>
    </div>
  );
}
